import React, { createContext, useContext, useState } from 'react';

interface EditGroupContextValue {
    setEditingCard: (groupName: string, cardId: string | null) => void;
    isEditing: (groupName: string, cardId: string) => boolean;
    isEditingDisabled: (groupName: string, cardId: string) => boolean;
}

const EditableCardGroupContext = createContext<EditGroupContextValue | undefined>(undefined);

export const useEditableCardGroup = () => {
    const context = useContext(EditableCardGroupContext);
    if (!context) {
        throw new Error('useEditableCardGroup must be used within an EditableCardGroupProvider');
    }
    return context;
};

interface EditableCardGroupProviderProps {
    children: React.ReactNode;
}

export const EditableCardGroupProvider = ({ children }: EditableCardGroupProviderProps) => {
    // Track currently editing card per group: { groupName: cardId | null }
    const [editingCardsByGroup, setEditingCardsByGroup] = useState<Record<string, string | null>>({});

    const setEditingCard = (groupName: string, cardId: string | null) => {
        setEditingCardsByGroup((prev) => ({
            ...prev,
            [groupName]: cardId,
        }));
    };

    const isEditing = (groupName: string, cardId: string) => {
        return editingCardsByGroup[groupName] === cardId;
    };

    const isEditingDisabled = (groupName: string, cardId: string) => {
        const currentlyEditingInGroup = editingCardsByGroup[groupName];
        // Should be disabled if another card in the group is currently editing
        return currentlyEditingInGroup != null && currentlyEditingInGroup !== cardId;
    };

    const value: EditGroupContextValue = {
        setEditingCard,
        isEditing,
        isEditingDisabled,
    };

    return <EditableCardGroupContext.Provider value={value}>{children}</EditableCardGroupContext.Provider>;
};
